import { Button } from '@chiroup/components/Button';
import { ConfirmModal } from '@chiroup/components/ConfirmModal';
import { Checkbox } from '@chiroup/components/form/Checkbox';
import { Input } from '@chiroup/components/form/Input';
import { Select } from '@chiroup/components/form/Select';
import { Textarea } from '@chiroup/components/form/Textarea';
import {
  DEFAULT_EHR_TRIAL_DASHBOARD,
  EhrDashboardFaq,
  EhrReportingItem,
  EhrTrialDashboardContent,
} from '@chiroup/core/types/EhrTrialDashboard.type';
import {
  ClickBehavior,
  DEFAULT_TRIAL_HOME,
  mergeTrialHomeWithDefaults,
  TRIAL_CARD_ICON_OPTIONS,
  TRIAL_TOUR_MANIFESTS,
  TRIAL_TOUR_OPTIONS,
  TrialCardIcon,
  TrialCardSection,
  TrialHomeCard,
  TrialHomeContent,
  TrialTourManifest,
  TrialTourStepManifest,
} from '@chiroup/core/types/TrialHome.type';
import {
  DEFAULT_UNSURE_TRIAL_DASHBOARD,
  UnsureTrialDashboardContent,
} from '@chiroup/core/types/UnsureTrialDashboard.type';
import {
  ArrowDown as ArrowDownIcon,
  ArrowUp as ArrowUpIcon,
  Undo2 as ArrowUturnLeftIcon,
  ChartBar as ChartBarIcon,
  Star as StarIcon,
} from 'lucide-react';

import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import appDataService, { TrialExperienceMeta } from '../../services/app-data.service';
import Uploader from '../common/uploader/Uploader';
import Header from '../layout/Header';
import EhrTrialHomePreview from './EhrTrialHomePreview';
import TrialHomePreview from './TrialHomePreview';
import UnsureTrialHomePreview from './UnsureTrialHomePreview';

dayjs.extend(relativeTime);

// Storage keys. Editor reads/writes the *Draft keys; the deploy endpoint
// promotes them to the live keys (trialHome / trialTourOverrides) that
// subscribers read — same save-vs-publish split as the home page builder.
const HOME_DRAFT_KEY = 'trialHomeDraft';
const HOME_LIVE_KEY = 'trialHome';
const OVERRIDES_DRAFT_KEY = 'trialTourOverridesDraft';
const OVERRIDES_LIVE_KEY = 'trialTourOverrides';
const META_KEY = 'trialExperienceMeta';

/**
 * Combined editor for the trial-user experience:
 *   - Home: page-level copy + card grid (the trial-home cards)
 *   - Tours: per-step copy overrides (title/body/proTip/nextLabel)
 *
 * Two-pane layout: left navigator selects one editable thing (page, card, or
 * tour-step), right pane is its form. Saves under two keys (`trialHome` for
 * card content, `trialTourOverrides` for tour copy); each side has independent
 * dirty tracking, so Save only writes what's actually changed.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type StepOverride = {
  title?: string;
  body?: string;
  proTip?: string;
  nextLabel?: string;
  video?: string;
};

type OverridesByTour = Record<string, Record<string, StepOverride>>;

type Selection =
  | { kind: 'home-page' }
  | { kind: 'home-card'; cardId: string }
  | { kind: 'ehr-dashboard'; section: EhrDashboardSectionKey }
  | { kind: 'unsure-dashboard'; section: UnsureDashboardSectionKey }
  | { kind: 'tour-step'; tourId: string; stepKey: string };

/** One sidebar row per section of the EHR trial dashboard, in page order. */
const EHR_DASHBOARD_SECTIONS = [
  { key: 'saop', label: 'The ChiroUp difference', hint: 'SAOP intro · Why SAOP video' },
  { key: 'roadmap', label: 'Learn & Demo roadmap', hint: 'Five steps · videos · reports' },
  { key: 'walkthrough', label: 'Personalized walkthrough', hint: 'Conversion CTA' },
  { key: 'switching', label: 'Switching & onboarding', hint: 'Testimonial · FAQs' },
  { key: 'essentials', label: 'Clinical tools', hint: 'Essentials teaser' },
] as const;

type EhrDashboardSectionKey = (typeof EHR_DASHBOARD_SECTIONS)[number]['key'];

/** One sidebar row per section of the unsure trial dashboard, in page order.
 *  Copy only: the tool cards are the Essentials cards above, and the workflow's
 *  videos and report list are edited under EHR dashboard > Learn & Demo. */
const UNSURE_DASHBOARD_SECTIONS = [
  { key: 'intro', label: 'Introduction', hint: 'Greeting copy · no CTA' },
  { key: 'tools', label: 'Clinical tools', hint: 'Green path · Essentials cards' },
  { key: 'ehr', label: 'Complete EHR', hint: 'Orange path · workflow · SAOP' },
  { key: 'walkthrough', label: 'Schedule a walkthrough', hint: 'Blue path · conversion CTA' },
] as const;

type UnsureDashboardSectionKey = (typeof UNSURE_DASHBOARD_SECTIONS)[number]['key'];

const SECTION_OPTIONS = [
  { text: 'Essentials', value: 'essentials' },
  { text: 'EHR', value: 'ehr' },
];

const CLICK_KIND_OPTIONS = [
  { text: 'Start an in-app tour', value: 'tour' },
  { text: 'Play a video (YouTube / Loom)', value: 'video' },
  { text: 'Navigate to a route', value: 'route' },
  { text: 'Open an external link', value: 'external' },
  { text: 'No action (informational)', value: 'none' },
];

// ---------------------------------------------------------------------------
// Main editor
// ---------------------------------------------------------------------------

const TrialExperienceEditor = () => {
  // ---- Home content ----
  const [homeData, setHomeData] = useState<TrialHomeContent>(DEFAULT_TRIAL_HOME);
  const [savedHomeSnapshot, setSavedHomeSnapshot] = useState(JSON.stringify(DEFAULT_TRIAL_HOME));

  // ---- Tour overrides ----
  const [overrides, setOverrides] = useState<OverridesByTour>({});
  const [savedOverridesSnapshot, setSavedOverridesSnapshot] = useState('{}');

  // ---- UI state ----
  const [selection, setSelection] = useState<Selection>({ kind: 'home-page' });
  // Tours start collapsed so the sidebar isn't a wall of step rows on first open.
  const [expandedTours, setExpandedTours] = useState<Set<string>>(new Set());
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [deployModalOpen, setDeployModalOpen] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  // Tracks publishedAt / lastEditedAt so the toolbar pill can distinguish
  // "Unsaved" (in-memory dirty), "Unpublished changes" (saved as draft but
  // not yet deployed), and "Up to date".
  const [meta, setMeta] = useState<TrialExperienceMeta | null>(null);

  // Load drafts + live + meta in parallel on mount. Editor prefers the
  // draft (last work-in-progress); if no draft exists yet, fall back to
  // live so admins keep editing where they left off.
  useEffect(() => {
    let destroyed = false;

    const load = async () => {
      const [homeDraftRes, homeLiveRes, overrideDraftRes, overrideLiveRes, metaRes] =
        await Promise.allSettled([
          appDataService.get(HOME_DRAFT_KEY),
          appDataService.get(HOME_LIVE_KEY),
          appDataService.get(OVERRIDES_DRAFT_KEY),
          appDataService.get(OVERRIDES_LIVE_KEY),
          appDataService.get(META_KEY),
        ]);

      if (destroyed) return;

      const homeDraft = homeDraftRes.status === 'fulfilled' ? homeDraftRes.value : null;
      const homeLive = homeLiveRes.status === 'fulfilled' ? homeLiveRes.value : null;
      const home = homeDraft || homeLive;
      if (home) {
        const merged = mergeTrialHomeWithDefaults(home);
        setHomeData(merged);
        setSavedHomeSnapshot(JSON.stringify(merged));
      }

      const overrideDraft = overrideDraftRes.status === 'fulfilled' ? overrideDraftRes.value : null;
      const overrideLive = overrideLiveRes.status === 'fulfilled' ? overrideLiveRes.value : null;
      const overridesSource = overrideDraft || overrideLive;
      if (overridesSource) {
        setOverrides(overridesSource as OverridesByTour);
        setSavedOverridesSnapshot(JSON.stringify(overridesSource));
      }

      if (metaRes.status === 'fulfilled' && metaRes.value) {
        setMeta(metaRes.value as TrialExperienceMeta);
      }
    };

    load();

    return () => {
      destroyed = true;
    };
  }, []);

  // ---- Dirty tracking ----
  const homeIsDirty = useMemo(
    () => JSON.stringify(homeData) !== savedHomeSnapshot,
    [homeData, savedHomeSnapshot]
  );
  const toursIsDirty = useMemo(
    () => JSON.stringify(overrides) !== savedOverridesSnapshot,
    [overrides, savedOverridesSnapshot]
  );
  const isDirty = homeIsDirty || toursIsDirty;

  // ---- Save (writes to DRAFT keys — subscribers don't see anything until
  // Deploy is clicked) ----
  const save = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      const writes: Promise<unknown>[] = [];
      if (homeIsDirty) writes.push(appDataService.update(HOME_DRAFT_KEY, homeData));

      // Strip empty fields before persisting so the override blob stays
      // compact and `Object.keys(...)` checks remain meaningful at render.
      const cleaned = pruneEmpty(overrides);
      if (toursIsDirty) writes.push(appDataService.update(OVERRIDES_DRAFT_KEY, cleaned));

      await Promise.all(writes);

      if (homeIsDirty) setSavedHomeSnapshot(JSON.stringify(homeData));
      if (toursIsDirty) {
        setOverrides(cleaned);
        setSavedOverridesSnapshot(JSON.stringify(cleaned));
      }
      // Mirror the backend stamp client-side so the toolbar's
      // "Unpublished changes" indicator flips immediately without a refetch.
      setMeta(prev => ({ ...(prev ?? {}), lastEditedAt: new Date().toISOString() }));
      setSaveMessage('Saved!');
      setTimeout(() => setSaveMessage(''), 2000);
    } catch {
      setSaveMessage('Error saving');
    } finally {
      setIsSaving(false);
    }
  };

  // ---- Deploy (promotes drafts to live in one transaction) ----
  const deploy = async () => {
    setIsDeploying(true);
    try {
      const res = await appDataService.deployTrialExperience();
      setMeta(prev => ({
        ...(prev ?? {}),
        publishedAt: res?.publishedAt ?? new Date().toISOString(),
        deployedBy: res?.deployedBy,
      }));
      setDeployModalOpen(false);
      setSaveMessage(res?.noChanges ? 'Nothing to deploy' : 'Deployed!');
      setTimeout(() => setSaveMessage(''), 2500);
    } catch {
      setSaveMessage('Error deploying');
    } finally {
      setIsDeploying(false);
    }
  };

  // Deploy status pill: "Unsaved" (in-memory dirty) > "Unpublished" (saved
  // but not deployed) > "Up to date".
  const deployStatus = useMemo(() => {
    const published = meta?.publishedAt ? dayjs(meta.publishedAt) : null;
    const edited = meta?.lastEditedAt ? dayjs(meta.lastEditedAt) : null;
    const hasUnpublished = !!edited && (!published || edited.isAfter(published));

    return {
      hasUnpublished,
      publishedAgo: published ? published.fromNow() : null,
      editedAgo: edited ? edited.fromNow() : null,
    };
  }, [meta]);

  // ---- Home mutators ----
  const updateHomeField = <K extends keyof TrialHomeContent>(
    field: K,
    value: TrialHomeContent[K]
  ) => setHomeData(prev => ({ ...prev, [field]: value }));

  // The EHR dashboard's content is one nested block on the same stored row, so
  // its edits go through the same dirty tracking and Save as everything else.
  const updateEhrField = <K extends keyof EhrTrialDashboardContent>(
    field: K,
    value: EhrTrialDashboardContent[K]
  ) =>
    setHomeData(prev => ({
      ...prev,
      ehrDashboard: {
        ...(prev.ehrDashboard ?? DEFAULT_EHR_TRIAL_DASHBOARD),
        [field]: value,
      },
    }));

  // Same nesting, same stored row, same dirty tracking as the EHR block.
  const updateUnsureField = <K extends keyof UnsureTrialDashboardContent>(
    field: K,
    value: UnsureTrialDashboardContent[K]
  ) =>
    setHomeData(prev => ({
      ...prev,
      unsureDashboard: {
        ...(prev.unsureDashboard ?? DEFAULT_UNSURE_TRIAL_DASHBOARD),
        [field]: value,
      },
    }));

  const updateCard = (id: string, patch: Partial<TrialHomeCard>) =>
    setHomeData(prev => ({
      ...prev,
      cards: prev.cards.map(c => (c.id === id ? { ...c, ...patch } : c)),
    }));

  const reorderCard = (id: string, dir: 'up' | 'down') =>
    setHomeData(prev => {
      const card = prev.cards.find(c => c.id === id);
      if (!card) return prev;
      const sectionCards = prev.cards.filter(c => c.section === card.section);
      const idx = sectionCards.findIndex(c => c.id === id);
      const swapWith = sectionCards[dir === 'up' ? idx - 1 : idx + 1];
      if (!swapWith) return prev;

      const aIdx = prev.cards.findIndex(c => c.id === card.id);
      const bIdx = prev.cards.findIndex(c => c.id === swapWith.id);
      const next = [...prev.cards];
      [next[aIdx], next[bIdx]] = [next[bIdx], next[aIdx]];

      return { ...prev, cards: next };
    });

  const addCard = (section: TrialCardSection) => {
    const id = `custom-${Math.random().toString(36).slice(2, 8)}`;
    const newCard: TrialHomeCard = {
      id,
      enabled: true,
      section,
      icon: 'sparkles',
      title: 'New card',
      description: 'Describe this card.',
      clickBehavior: { kind: 'none' },
    };
    setHomeData(prev => ({ ...prev, cards: [...prev.cards, newCard] }));
    setSelection({ kind: 'home-card', cardId: id });
  };

  const removeCard = (id: string) => {
    setHomeData(prev => ({ ...prev, cards: prev.cards.filter(c => c.id !== id) }));
    // If the deleted card was selected, fall back to the page form.
    setSelection(prev =>
      prev.kind === 'home-card' && prev.cardId === id ? { kind: 'home-page' } : prev
    );
  };

  // ---- Tour mutators ----
  const setStepField = (
    tourId: string,
    stepKey: string,
    field: keyof StepOverride,
    value: string
  ) => {
    setOverrides(prev => {
      const tour = { ...(prev[tourId] || {}) };
      const step = { ...(tour[stepKey] || {}) };
      // Empty string means "no override" — store as missing so the code
      // default is used at render time.
      if (value.trim() === '') {
        delete step[field];
      } else {
        step[field] = value;
      }
      tour[stepKey] = step;

      return { ...prev, [tourId]: tour };
    });
  };

  const resetStep = (tourId: string, stepKey: string) => {
    setOverrides(prev => {
      const tour = { ...(prev[tourId] || {}) };
      delete tour[stepKey];

      return { ...prev, [tourId]: tour };
    });
  };

  const toggleTour = (tourId: string) =>
    setExpandedTours(prev => {
      const next = new Set(prev);
      if (next.has(tourId)) next.delete(tourId);
      else next.add(tourId);

      return next;
    });

  // Auto-expand a tour when one of its steps is selected, so the navigator
  // stays in sync with the editor pane.
  const selectStep = (tourId: string, stepKey: string) => {
    setExpandedTours(prev => {
      if (prev.has(tourId)) return prev;
      const next = new Set(prev);
      next.add(tourId);

      return next;
    });
    setSelection({ kind: 'tour-step', tourId, stepKey });
  };

  return (
    <>
      <Header
        title="Trial Experience"
        rightSide={
          <div className="flex items-center gap-3">
            <DeployStatusPill
              isDirty={isDirty}
              hasUnpublished={deployStatus.hasUnpublished}
              saveMessage={saveMessage}
            />
            <Link
              to="/home-page/trial-experience/analytics"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 hover:bg-gray-50 dark:hover:bg-dark-gray-700 text-sm font-medium text-gray-700 dark:text-dark-gray-200 transition-colors"
            >
              <ChartBarIcon className="h-4 w-4" />
              Analytics
            </Link>
            <button
              type="button"
              onClick={() => setPreviewOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-gray-300 dark:border-dark-gray-600 bg-white dark:bg-dark-gray-800 hover:bg-gray-50 dark:hover:bg-dark-gray-700 text-sm font-medium text-gray-700 dark:text-dark-gray-200 transition-colors"
            >
              <Icon icon={Icons.eye} className="h-4 w-4" />
              Preview home
            </button>
            <Button
              text={isDirty ? 'Save draft' : 'Saved'}
              onClick={save}
              loading={isSaving}
              disabled={!isDirty || isDeploying}
            />
            <button
              type="button"
              onClick={() => setDeployModalOpen(true)}
              disabled={isDirty || !deployStatus.hasUnpublished || isDeploying || isSaving}
              title={
                isDirty
                  ? 'Save your draft before deploying.'
                  : !deployStatus.hasUnpublished
                    ? 'No changes to deploy.'
                    : 'Make this draft live for all trial users.'
              }
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Icon icon={Icons.rocketLaunch} className="h-4 w-4" />
              Deploy
            </button>
          </div>
        }
      />
      <div className="flex gap-4 m-4 min-h-[calc(100vh-8rem)]">
        <Sidebar
          homeData={homeData}
          overrides={overrides}
          selection={selection}
          expandedTours={expandedTours}
          onSelect={setSelection}
          onToggleTour={toggleTour}
          onAddCard={addCard}
        />
        <main className="flex-1 min-w-0">
          <EditorPane
            selection={selection}
            homeData={homeData}
            overrides={overrides}
            onUpdateHomeField={updateHomeField}
            onUpdateEhrField={updateEhrField}
            onUpdateUnsureField={updateUnsureField}
            onUpdateCard={updateCard}
            onReorderCard={reorderCard}
            onRemoveCard={removeCard}
            onChangeStepField={setStepField}
            onResetStep={resetStep}
          />
        </main>
      </div>

      <PreviewModal
        open={previewOpen}
        data={homeData}
        selectedCardId={selection.kind === 'home-card' ? selection.cardId : null}
        variant={
          selection.kind === 'ehr-dashboard'
            ? 'ehr'
            : selection.kind === 'unsure-dashboard'
              ? 'unsure'
              : 'cards'
        }
        onSelectCard={id => {
          setSelection({ kind: 'home-card', cardId: id });
          setPreviewOpen(false);
        }}
        onClose={() => setPreviewOpen(false)}
      />

      <ConfirmModal
        isOpen={deployModalOpen}
        title="Deploy trial experience?"
        confirmText={isDeploying ? 'Deploying...' : 'Deploy'}
        cancelText="Cancel"
        loading={isDeploying}
        confirmDisabled={isDeploying}
        confirm={deploy}
        close={() => !isDeploying && setDeployModalOpen(false)}
        description={
          <span>
            This will replace the live trial home + tour copy for every clinic on the trial plan
            with your current draft.
          </span>
        }
      >
        <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-left">
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5">
            <dt className="text-gray-500">Last deployed</dt>
            <dd className="font-medium text-gray-900">{deployStatus.publishedAgo ?? 'Never'}</dd>
            <dt className="text-gray-500">Last edited</dt>
            <dd className="font-medium text-gray-900">{deployStatus.editedAgo ?? '—'}</dd>
          </dl>
        </div>
      </ConfirmModal>
    </>
  );
};

// ---------------------------------------------------------------------------
// Sidebar
// ---------------------------------------------------------------------------

const Sidebar: React.FC<{
  homeData: TrialHomeContent;
  overrides: OverridesByTour;
  selection: Selection;
  expandedTours: Set<string>;
  onSelect: (s: Selection) => void;
  onToggleTour: (tourId: string) => void;
  onAddCard: (section: TrialCardSection) => void;
}> = ({ homeData, overrides, selection, expandedTours, onSelect, onToggleTour, onAddCard }) => {
  const isPageSelected = selection.kind === 'home-page';

  return (
    <aside className="w-72 shrink-0 bg-white dark:bg-dark-gray-800 border border-gray-200 dark:border-dark-gray-700 rounded-lg overflow-y-auto sticky top-4 self-start max-h-[calc(100vh-6rem)]">
      <SidebarGroup label="Home">
        <SidebarRow
          label="Page settings"
          hint="Heading · section labels · CTA"
          selected={isPageSelected}
          onClick={() => onSelect({ kind: 'home-page' })}
        />
      </SidebarGroup>

      <SidebarSubgroup label="Essentials feature cards" onAdd={() => onAddCard('essentials')}>
        {homeData.cards
          .filter(c => c.section === 'essentials')
          .map(card => (
            <SidebarRow
              key={card.id}
              label={card.title || 'Untitled card'}
              hint={card.enabled ? undefined : 'Hidden'}
              selected={selection.kind === 'home-card' && selection.cardId === card.id}
              dim={!card.enabled}
              onClick={() => onSelect({ kind: 'home-card', cardId: card.id })}
            />
          ))}
      </SidebarSubgroup>

      <SidebarSubgroup label="EHR feature cards" onAdd={() => onAddCard('ehr')}>
        {homeData.cards
          .filter(c => c.section === 'ehr')
          .map(card => (
            <SidebarRow
              key={card.id}
              label={card.title || 'Untitled card'}
              hint={card.enabled ? undefined : 'Hidden'}
              selected={selection.kind === 'home-card' && selection.cardId === card.id}
              dim={!card.enabled}
              onClick={() => onSelect({ kind: 'home-card', cardId: card.id })}
            />
          ))}
      </SidebarSubgroup>

      <SidebarGroup label="EHR dashboard" topBorder>
        {EHR_DASHBOARD_SECTIONS.map(section => (
          <SidebarRow
            key={section.key}
            label={section.label}
            hint={section.hint}
            selected={selection.kind === 'ehr-dashboard' && selection.section === section.key}
            onClick={() => onSelect({ kind: 'ehr-dashboard', section: section.key })}
          />
        ))}
      </SidebarGroup>

      <SidebarGroup label="Unsure dashboard" topBorder>
        {UNSURE_DASHBOARD_SECTIONS.map(section => (
          <SidebarRow
            key={section.key}
            label={section.label}
            hint={section.hint}
            selected={selection.kind === 'unsure-dashboard' && selection.section === section.key}
            onClick={() => onSelect({ kind: 'unsure-dashboard', section: section.key })}
          />
        ))}
      </SidebarGroup>

      <SidebarGroup label="Tours" topBorder>
        {TRIAL_TOUR_MANIFESTS.map(tour => {
          const expanded = expandedTours.has(tour.id);
          const tourOverrides = overrides[tour.id] || {};
          const editedCount = Object.keys(tourOverrides).filter(stepKey =>
            hasAnyOverride(tourOverrides[stepKey])
          ).length;

          return (
            <div key={tour.id} className="px-1">
              <button
                type="button"
                onClick={() => onToggleTour(tour.id)}
                className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-left text-sm text-gray-700 dark:text-dark-gray-200 hover:bg-gray-50 dark:hover:bg-dark-gray-700 transition-colors"
              >
                {expanded ? (
                  <Icon icon={Icons.chevronDown} className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                ) : (
                  <Icon icon={Icons.chevronRight} className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                )}
                <span className="flex-1 truncate">{tour.label}</span>
                {editedCount > 0 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                    {editedCount}
                  </span>
                )}
              </button>
              {expanded && (
                <div className="pl-5 mt-0.5 mb-1 flex flex-col gap-0.5">
                  {tour.steps.map((step, i) => {
                    const isSelected =
                      selection.kind === 'tour-step' &&
                      selection.tourId === tour.id &&
                      selection.stepKey === step.stepKey;
                    const edited = hasAnyOverride(tourOverrides[step.stepKey]);

                    return (
                      <SidebarRow
                        key={step.stepKey}
                        label={`${i + 1}. ${step.title || step.stepKey}`}
                        selected={isSelected}
                        edited={edited}
                        size="sm"
                        onClick={() =>
                          onSelect({ kind: 'tour-step', tourId: tour.id, stepKey: step.stepKey })
                        }
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </SidebarGroup>
    </aside>
  );
};

const SidebarGroup: React.FC<{
  label: string;
  children: React.ReactNode;
  topBorder?: boolean;
}> = ({ label, children, topBorder }) => (
  <div
    className={
      topBorder ? 'pt-3 pb-2 border-t border-gray-100 dark:border-dark-gray-700' : 'pt-3 pb-2'
    }
  >
    <div className="px-3 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-dark-gray-500">
      {label}
    </div>
    {children}
  </div>
);

const SidebarSubgroup: React.FC<{
  label: string;
  onAdd: () => void;
  children: React.ReactNode;
}> = ({ label, onAdd, children }) => (
  <div className="px-1 pb-2">
    <div className="flex items-center justify-between px-2 py-1">
      <div className="text-[10px] font-medium uppercase tracking-wider text-gray-400 dark:text-dark-gray-500">
        {label}
      </div>
      <button
        type="button"
        onClick={onAdd}
        aria-label={`Add ${label} card`}
        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-dark-gray-700 text-gray-400 hover:text-gray-700 dark:hover:text-dark-gray-200 transition-colors"
      >
        <Icon icon={Icons.add} className="h-3.5 w-3.5" />
      </button>
    </div>
    <div className="flex flex-col gap-0.5">{children}</div>
  </div>
);

const SidebarRow: React.FC<{
  label: string;
  hint?: string;
  selected: boolean;
  dim?: boolean;
  edited?: boolean;
  size?: 'sm' | 'md';
  onClick: () => void;
}> = ({ label, hint, selected, dim, edited, size = 'md', onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={[
      'w-full text-left px-2 rounded-md transition-colors',
      size === 'sm' ? 'py-1' : 'py-1.5',
      selected
        ? 'bg-primary-600 text-white hover:bg-primary-700'
        : 'text-gray-700 dark:text-dark-gray-200 hover:bg-gray-50 dark:hover:bg-dark-gray-700',
      dim ? 'opacity-60' : '',
    ].join(' ')}
  >
    <div className="flex items-center gap-2">
      <span className={['flex-1 truncate', size === 'sm' ? 'text-xs' : 'text-sm'].join(' ')}>
        {label}
      </span>
      {edited && (
        <span
          className={[
            'h-1.5 w-1.5 rounded-full shrink-0',
            selected ? 'bg-white' : 'bg-amber-500',
          ].join(' ')}
        />
      )}
    </div>
    {hint && (
      <div
        className={[
          'truncate text-[11px]',
          selected ? 'text-white/80' : 'text-gray-400 dark:text-dark-gray-500',
        ].join(' ')}
      >
        {hint}
      </div>
    )}
  </button>
);

// ---------------------------------------------------------------------------
// Editor pane — switches on selection
// ---------------------------------------------------------------------------

const EditorPane: React.FC<{
  selection: Selection;
  homeData: TrialHomeContent;
  overrides: OverridesByTour;
  onUpdateHomeField: <K extends keyof TrialHomeContent>(
    field: K,
    value: TrialHomeContent[K]
  ) => void;
  onUpdateUnsureField: <K extends keyof UnsureTrialDashboardContent>(
    field: K,
    value: UnsureTrialDashboardContent[K]
  ) => void;
  onUpdateEhrField: <K extends keyof EhrTrialDashboardContent>(
    field: K,
    value: EhrTrialDashboardContent[K]
  ) => void;
  onUpdateCard: (id: string, patch: Partial<TrialHomeCard>) => void;
  onReorderCard: (id: string, dir: 'up' | 'down') => void;
  onRemoveCard: (id: string) => void;
  onChangeStepField: (
    tourId: string,
    stepKey: string,
    field: keyof StepOverride,
    value: string
  ) => void;
  onResetStep: (tourId: string, stepKey: string) => void;
}> = ({
  selection,
  homeData,
  overrides,
  onUpdateHomeField,
  onUpdateEhrField,
  onUpdateUnsureField,
  onUpdateCard,
  onReorderCard,
  onRemoveCard,
  onChangeStepField,
  onResetStep,
}) => {
  if (selection.kind === 'home-page') {
    return <HomePageForm data={homeData} onChange={onUpdateHomeField} />;
  }

  if (selection.kind === 'home-card') {
    const card = homeData.cards.find(c => c.id === selection.cardId);
    if (!card) {
      return <EmptyState message="This card no longer exists. Pick something on the left." />;
    }
    const sectionCards = homeData.cards.filter(c => c.section === card.section);
    const idx = sectionCards.findIndex(c => c.id === card.id);

    return (
      <HomeCardForm
        card={card}
        canMoveUp={idx > 0}
        canMoveDown={idx < sectionCards.length - 1}
        onUpdate={patch => onUpdateCard(card.id, patch)}
        onMoveUp={() => onReorderCard(card.id, 'up')}
        onMoveDown={() => onReorderCard(card.id, 'down')}
        onDelete={() => {
          if (window.confirm(`Delete "${card.title || card.id}"?`)) {
            onRemoveCard(card.id);
          }
        }}
      />
    );
  }

  if (selection.kind === 'ehr-dashboard') {
    return (
      <EhrDashboardForm
        section={selection.section}
        data={homeData.ehrDashboard ?? DEFAULT_EHR_TRIAL_DASHBOARD}
        onChange={onUpdateEhrField}
      />
    );
  }

  if (selection.kind === 'unsure-dashboard') {
    return (
      <UnsureDashboardForm
        section={selection.section}
        data={homeData.unsureDashboard ?? DEFAULT_UNSURE_TRIAL_DASHBOARD}
        ehrData={homeData.ehrDashboard ?? DEFAULT_EHR_TRIAL_DASHBOARD}
        onChange={onUpdateUnsureField}
      />
    );
  }

  // selection.kind === 'tour-step'
  const tour = TRIAL_TOUR_MANIFESTS.find(t => t.id === selection.tourId);
  const step = tour?.steps.find(s => s.stepKey === selection.stepKey);
  if (!tour || !step) {
    return <EmptyState message="This step no longer exists. Pick something on the left." />;
  }
  const stepNumber = tour.steps.findIndex(s => s.stepKey === selection.stepKey) + 1;
  const override = overrides[tour.id]?.[step.stepKey] || {};

  return (
    <TourStepForm
      tour={tour}
      step={step}
      stepNumber={stepNumber}
      override={override}
      onChangeField={(field, value) => onChangeStepField(tour.id, step.stepKey, field, value)}
      onReset={() => onResetStep(tour.id, step.stepKey)}
    />
  );
};

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex items-center justify-center h-full">
    <p className="text-sm text-gray-500 dark:text-dark-gray-400 italic">{message}</p>
  </div>
);

// ---------------------------------------------------------------------------
// Home page form (heading / section labels / CTA)
// ---------------------------------------------------------------------------

const HomePageForm: React.FC<{
  data: TrialHomeContent;
  onChange: <K extends keyof TrialHomeContent>(field: K, value: TrialHomeContent[K]) => void;
}> = ({ data, onChange }) => (
  <Panel title="Page settings" subtitle="Shown at the top of every trial user's home view.">
    <Input
      name="heading"
      label="Page heading"
      value={data.heading}
      onChange={val => onChange('heading', val)}
    />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
      <Input
        name="essentialsLabel"
        label="Essentials section label"
        value={data.essentialsLabel}
        onChange={val => onChange('essentialsLabel', val)}
      />
      <Input
        name="ehrLabel"
        label="EHR section label"
        value={data.ehrLabel}
        onChange={val => onChange('ehrLabel', val)}
      />
    </div>

    <Divider />

    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
      Kickstart hero
    </h3>
    <Input
      name="kickstartHeading"
      label="Heading"
      value={data.kickstartHeading || ''}
      onChange={val => onChange('kickstartHeading', val)}
    />

    <div className="mt-4 rounded-lg border border-gray-200 dark:border-dark-gray-700 p-4">
      <Checkbox
        name="kickstartTestimonialEnabled"
        label="Show testimonial"
        value={data.kickstartTestimonialEnabled ?? true}
        onChange={val => onChange('kickstartTestimonialEnabled', val)}
      />
      <Textarea
        name="kickstartTestimonial"
        label="Review text"
        value={data.kickstartTestimonial || ''}
        onChange={val => onChange('kickstartTestimonial', val)}
        rows={3}
        className="mt-3"
      />
      <Input
        name="kickstartTestimonialAuthor"
        label="Reviewer name"
        value={data.kickstartTestimonialAuthor || ''}
        onChange={val => onChange('kickstartTestimonialAuthor', val)}
        className="mt-3"
      />
      <Uploader
        name="kickstartTestimonialAvatar"
        label="Reviewer avatar"
        className="mt-3"
        value={data.kickstartTestimonialAvatar || ''}
        onChange={val => onChange('kickstartTestimonialAvatar', val ?? '')}
        type="image"
        uploadCategory="loginContent"
        uploadType="image"
        fileTypes={['png', 'jpg', 'jpeg', 'gif', 'webp']}
        fileSizeLimit={10}
        square
      />
      <div className="mt-3">
        <span className="block text-sm font-medium text-gray-700 dark:text-dark-gray-300 mb-1">
          Star rating
        </span>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              type="button"
              onClick={() => onChange('kickstartTestimonialRating', n)}
              aria-label={`${n} star${n > 1 ? 's' : ''}`}
            >
              <StarIcon
                className={`h-6 w-6 ${
                  (data.kickstartTestimonialRating ?? 5) >= n
                    ? 'text-yellow-400'
                    : 'text-gray-300 dark:text-dark-gray-600'
                }`}
                fill={(data.kickstartTestimonialRating ?? 5) >= n ? 'currentColor' : 'none'}
              />
            </button>
          ))}
        </div>
      </div>
    </div>

    <Divider />

    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
      Bottom CTA
    </h3>
    <Input
      name="ctaTitle"
      label="Title"
      value={data.ctaTitle}
      onChange={val => onChange('ctaTitle', val)}
    />
    <Input
      name="ctaButton"
      label="Button text"
      value={data.ctaButton}
      onChange={val => onChange('ctaButton', val)}
      className="mt-3"
    />
    <Textarea
      name="ctaSubtitle"
      label="Subtitle (small fine-print under the button)"
      value={data.ctaSubtitle || ''}
      onChange={val => onChange('ctaSubtitle', val)}
      rows={2}
      className="mt-3"
    />
    <Input
      name="scheduleCallUrl"
      label="Schedule-a-call link — Trial / default (opens embedded; used by this button and the checklist CTA)"
      value={data.scheduleCallUrl || ''}
      onChange={val => onChange('scheduleCallUrl', val)}
      className="mt-3"
    />
    <Input
      name="scheduleCallUrlBasic"
      label="Schedule-a-call link — Basic (blank = use the Trial / default link)"
      value={data.scheduleCallUrlBasic || ''}
      onChange={val => onChange('scheduleCallUrlBasic', val)}
      className="mt-3"
    />
    <Input
      name="scheduleCallUrlPlus"
      label="Schedule-a-call link — Plus (blank = use the Trial / default link)"
      value={data.scheduleCallUrlPlus || ''}
      onChange={val => onChange('scheduleCallUrlPlus', val)}
      className="mt-3"
    />
    <Input
      name="scheduleCallUrlPremium"
      label="Schedule-a-call link — Premium (blank = use the Trial / default link)"
      value={data.scheduleCallUrlPremium || ''}
      onChange={val => onChange('scheduleCallUrlPremium', val)}
      className="mt-3"
    />

    <Divider />

    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
      Checklist bonus step
    </h3>
    <div className="rounded-lg border border-gray-200 dark:border-dark-gray-700 p-4">
      <Checkbox
        name="bonusStepEnabled"
        label="Show bonus step in the checklist"
        value={data.bonusStepEnabled ?? true}
        onChange={val => onChange('bonusStepEnabled', val)}
      />
      <Input
        name="bonusStepTitle"
        label="Title"
        value={data.bonusStepTitle || ''}
        onChange={val => onChange('bonusStepTitle', val)}
        className="mt-3"
      />
      <Textarea
        name="bonusStepSubtitle"
        label="Subtitle"
        value={data.bonusStepSubtitle || ''}
        onChange={val => onChange('bonusStepSubtitle', val)}
        rows={2}
        className="mt-3"
      />
      <Input
        name="bonusStepBadge"
        label="Badge"
        value={data.bonusStepBadge || ''}
        onChange={val => onChange('bonusStepBadge', val)}
        className="mt-3"
      />
      <Input
        name="bonusStepButtonText"
        label="Button label"
        value={data.bonusStepButtonText || ''}
        onChange={val => onChange('bonusStepButtonText', val)}
        className="mt-3"
      />
      <Uploader
        name="bonusStepImage"
        label="Expert photo"
        className="mt-3"
        value={data.bonusStepImage || ''}
        onChange={val => onChange('bonusStepImage', val ?? '')}
        type="image"
        uploadCategory="loginContent"
        uploadType="image"
        fileTypes={['png', 'jpg', 'jpeg', 'gif', 'webp']}
        fileSizeLimit={10}
        publicFile
        previewClassName="h-24 w-24 rounded-full object-cover"
        customLabel={data.bonusStepImage ? 'Remove' : undefined}
        onClickCustom={() => onChange('bonusStepImage', '')}
      />
      <p className="mt-2 text-xs text-gray-500 dark:text-dark-gray-400">
        Opens the schedule-a-call link above. It's a bonus — it doesn't count toward checklist
        completion.
      </p>
    </div>

    <Divider />

    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
      Checklist: Select my plan
    </h3>
    <div className="rounded-lg border border-gray-200 dark:border-dark-gray-700 p-4">
      <Checkbox
        name="selectPlanEnabled"
        label="Show the Select my plan CTA"
        value={data.selectPlanEnabled ?? true}
        onChange={val => onChange('selectPlanEnabled', val)}
      />
      <Input
        name="selectPlanTitle"
        label="Title"
        value={data.selectPlanTitle || ''}
        onChange={val => onChange('selectPlanTitle', val)}
        className="mt-3"
      />
      <Textarea
        name="selectPlanSubtitle"
        label="Subtitle"
        value={data.selectPlanSubtitle || ''}
        onChange={val => onChange('selectPlanSubtitle', val)}
        rows={2}
        className="mt-3"
      />
      <p className="mt-2 text-xs text-gray-500 dark:text-dark-gray-400">
        Starts Essentials checkout. Shown to clinic admins only; doesn't count toward checklist
        completion.
      </p>
    </div>
  </Panel>
);

// ---------------------------------------------------------------------------
// EHR dashboard form — the home shown to trial users whose intent is `ehr`
// ---------------------------------------------------------------------------

const EhrDashboardForm: React.FC<{
  section: EhrDashboardSectionKey;
  data: EhrTrialDashboardContent;
  onChange: <K extends keyof EhrTrialDashboardContent>(
    field: K,
    value: EhrTrialDashboardContent[K]
  ) => void;
}> = ({ section, data, onChange }) => {
  if (section === 'saop') {
    return (
      <Panel
        title="The ChiroUp difference"
        subtitle="Section 1 — kept compact so the roadmap is reachable without scrolling"
      >
        <Input
          name="saopHeading"
          label="Heading"
          value={data.saopHeading}
          onChange={val => onChange('saopHeading', val)}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <Input
            name="saopSubhead"
            label="Subhead"
            value={data.saopSubhead}
            onChange={val => onChange('saopSubhead', val)}
          />
          <Input
            name="saopPrimaryCta"
            label="Primary CTA (launches the SAOP sandbox)"
            value={data.saopPrimaryCta}
            onChange={val => onChange('saopPrimaryCta', val)}
          />
        </div>
        <Textarea
          name="saopDescription"
          label="Description"
          value={data.saopDescription}
          onChange={val => onChange('saopDescription', val)}
          rows={2}
          className="mt-3"
        />

        <Divider />

        <SubHeading text="Why SAOP? video" />
        <Input
          name="saopSecondaryCta"
          label="Secondary CTA label"
          value={data.saopSecondaryCta}
          onChange={val => onChange('saopSecondaryCta', val)}
        />
        <Input
          name="saopVideoUrl"
          label="Video URL (YouTube / Loom / Vimeo share link)"
          value={data.saopVideoUrl}
          onChange={val => onChange('saopVideoUrl', val)}
          className="mt-3"
        />
        <FieldNote text="Leave the URL blank and the secondary CTA is hidden, so there's never a button that opens an empty player." />
      </Panel>
    );
  }

  if (section === 'roadmap') {
    return (
      <Panel
        title="Learn & Demo roadmap"
        subtitle="Section 2 — the primary interactive area, and the source of the explored count"
      >
        <Input
          name="learnHeading"
          label="Heading"
          value={data.learnHeading}
          onChange={val => onChange('learnHeading', val)}
        />
        <Textarea
          name="learnSupportingCopy"
          label="Supporting copy"
          value={data.learnSupportingCopy}
          onChange={val => onChange('learnSupportingCopy', val)}
          rows={2}
          className="mt-3"
        />

        <Divider />

        <SubHeading text="1 · Schedule" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            name="scheduleTitle"
            label="Step label"
            value={data.scheduleTitle}
            onChange={val => onChange('scheduleTitle', val)}
          />
          <Input
            name="scheduleCta"
            label="CTA"
            value={data.scheduleCta}
            onChange={val => onChange('scheduleCta', val)}
          />
        </div>
        <Input
          name="scheduleVideoUrl"
          label="Video URL"
          value={data.scheduleVideoUrl}
          onChange={val => onChange('scheduleVideoUrl', val)}
          className="mt-3"
        />

        <Divider />

        <SubHeading text="2 · SAOP initial note" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            name="saopInitialTitle"
            label="Step label"
            value={data.saopInitialTitle}
            onChange={val => onChange('saopInitialTitle', val)}
          />
          <Input
            name="saopInitialCta"
            label="CTA"
            value={data.saopInitialCta}
            onChange={val => onChange('saopInitialCta', val)}
          />
        </div>
        <FieldNote text="Launches the guided SAOP sandbox. This step completes when the prospect signs the note, so it has no video or threshold." />

        <Divider />

        <SubHeading text="2b · Follow-up note" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            name="followUpTitle"
            label="Step label"
            value={data.followUpTitle}
            onChange={val => onChange('followUpTitle', val)}
          />
          <Input
            name="followUpCta"
            label="CTA"
            value={data.followUpCta}
            onChange={val => onChange('followUpCta', val)}
          />
        </div>
        <Input
          name="followUpSupportingCopy"
          label="Supporting copy"
          value={data.followUpSupportingCopy}
          onChange={val => onChange('followUpSupportingCopy', val)}
          className="mt-3"
        />
        <FieldNote text="The follow-up experience ships in its own story. Until it does, this node shows as Coming soon and is left out of the explored count — this copy is staged for when it lands." />

        <Divider />

        <SubHeading text="3 · Billing" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            name="billingTitle"
            label="Step label"
            value={data.billingTitle}
            onChange={val => onChange('billingTitle', val)}
          />
          <Input
            name="billingCta"
            label="CTA"
            value={data.billingCta}
            onChange={val => onChange('billingCta', val)}
          />
        </div>
        <Input
          name="billingVideoUrl"
          label="Video URL"
          value={data.billingVideoUrl}
          onChange={val => onChange('billingVideoUrl', val)}
          className="mt-3"
        />

        <Divider />

        <SubHeading text="4 · Reporting" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            name="reportingTitle"
            label="Step label"
            value={data.reportingTitle}
            onChange={val => onChange('reportingTitle', val)}
          />
          <Input
            name="reportingCta"
            label="CTA"
            value={data.reportingCta}
            onChange={val => onChange('reportingCta', val)}
          />
        </div>
        <Input
          name="reportingModalTitle"
          label="Pop-up title"
          value={data.reportingModalTitle}
          onChange={val => onChange('reportingModalTitle', val)}
          className="mt-3"
        />
        <ReportingItemsEditor
          items={data.reportingItems}
          onChange={items => onChange('reportingItems', items)}
        />
        <FieldNote text="With no report types listed, the Reporting step shows as Coming soon rather than opening an empty pop-up." />

        <Divider />

        <SubHeading text="Speed callout" />
        <Input
          name="speedCalloutHeading"
          label="Heading"
          value={data.speedCalloutHeading}
          onChange={val => onChange('speedCalloutHeading', val)}
        />
        <Textarea
          name="speedCalloutCopy"
          label="Copy"
          value={data.speedCalloutCopy}
          onChange={val => onChange('speedCalloutCopy', val)}
          rows={2}
          className="mt-3"
        />

        <Divider />

        <SubHeading text="Completion" />
        <Input
          name="videoWatchThresholdSeconds"
          label="Seconds before a video or the reports pop-up counts as explored"
          type="number"
          value={data.videoWatchThresholdSeconds}
          onChange={val => onChange('videoWatchThresholdSeconds', Number(val) || 0)}
        />
        <FieldNote text="Embedded players don't report playback to us, so this measures time with the clip open. Set it below the length of the shortest video." />
      </Panel>
    );
  }

  if (section === 'walkthrough') {
    return (
      <Panel
        title="Personalized walkthrough"
        subtitle="Section 3 — never gated on roadmap completion"
      >
        <Input
          name="walkthroughHeading"
          label="Heading"
          value={data.walkthroughHeading}
          onChange={val => onChange('walkthroughHeading', val)}
        />
        <Textarea
          name="walkthroughSupportingCopy"
          label="Supporting copy"
          value={data.walkthroughSupportingCopy}
          onChange={val => onChange('walkthroughSupportingCopy', val)}
          rows={2}
          className="mt-3"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <Input
            name="walkthroughCta"
            label="Primary CTA"
            value={data.walkthroughCta}
            onChange={val => onChange('walkthroughCta', val)}
          />
          <Input
            name="walkthroughSocialProof"
            label="Social proof line"
            value={data.walkthroughSocialProof}
            onChange={val => onChange('walkthroughSocialProof', val)}
          />
        </div>
        <Divider />

        <SubHeading text="Social proof faces" />
        <AvatarUploaders
          avatars={data.walkthroughAvatars}
          onChange={avatars => onChange('walkthroughAvatars', avatars)}
        />
        <FieldNote text="Up to three faces show beside the social-proof line. With none uploaded, the line stands on its own rather than showing placeholder circles." />
        <FieldNote text="Opens the same scheduler as the rest of the trial experience — set the link under Page settings." />
      </Panel>
    );
  }

  if (section === 'switching') {
    return (
      <Panel
        title="Switching & onboarding"
        subtitle="Section 4 — expanded by default; reassurance, not sales"
      >
        <Input
          name="switchingHeading"
          label="Heading"
          value={data.switchingHeading}
          onChange={val => onChange('switchingHeading', val)}
        />
        <Textarea
          name="switchingSupportingCopy"
          label="Supporting copy"
          value={data.switchingSupportingCopy}
          onChange={val => onChange('switchingSupportingCopy', val)}
          rows={2}
          className="mt-3"
        />

        <Divider />

        <SubHeading text="Customer testimonial" />
        <Input
          name="testimonialHeadline"
          label="Headline (quotation marks are added for you)"
          value={data.testimonialHeadline}
          onChange={val => onChange('testimonialHeadline', val)}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <Input
            name="testimonialAuthor"
            label="Attribution (optional)"
            value={data.testimonialAuthor}
            onChange={val => onChange('testimonialAuthor', val)}
          />
          <Input
            name="testimonialCta"
            label="CTA"
            value={data.testimonialCta}
            onChange={val => onChange('testimonialCta', val)}
          />
        </div>
        <Textarea
          name="testimonialSupportingCopy"
          label="Supporting line (optional)"
          value={data.testimonialSupportingCopy}
          onChange={val => onChange('testimonialSupportingCopy', val)}
          rows={2}
          className="mt-3"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <Input
            name="testimonialVideoUrl"
            label="Video URL"
            value={data.testimonialVideoUrl}
            onChange={val => onChange('testimonialVideoUrl', val)}
          />
          <Input
            name="testimonialDuration"
            label='Runtime badge (e.g. "1:22")'
            value={data.testimonialDuration}
            onChange={val => onChange('testimonialDuration', val)}
          />
        </div>
        <Uploader
          name="testimonialThumbnail"
          label="Poster frame"
          className="mt-3"
          value={data.testimonialThumbnail}
          onChange={val => onChange('testimonialThumbnail', val ?? '')}
          type="image"
          uploadCategory="loginContent"
          uploadType="image"
          fileTypes={['png', 'jpg', 'jpeg', 'gif', 'webp']}
          fileSizeLimit={10}
        />
        <FieldNote text="Use a real, approved customer clip about switching or onboarding. Blank video URL hides the play button; the runtime is typed in because embeds don't report it." />

        <Divider />

        <SubHeading text="FAQs" />
        <FaqListEditor faqs={data.faqs} onChange={faqs => onChange('faqs', faqs)} />

        <Divider />

        <SubHeading text="Reassurance" />
        <Input
          name="reassuranceHeading"
          label="Heading"
          value={data.reassuranceHeading}
          onChange={val => onChange('reassuranceHeading', val)}
        />
        <Textarea
          name="reassuranceCopy"
          label="Copy"
          value={data.reassuranceCopy}
          onChange={val => onChange('reassuranceCopy', val)}
          rows={2}
          className="mt-3"
        />
        <StringListEditor
          label="Proof points"
          namePrefix="proofPoint"
          items={data.reassuranceProofPoints}
          onChange={items => onChange('reassuranceProofPoints', items)}
          addLabel="Add proof point"
        />
        <FieldNote text="Only claim a go-live window here while it stays operationally accurate." />
      </Panel>
    );
  }

  return (
    <Panel
      title="Clinical tools"
      subtitle="Section 5 — collapsed by default, deliberately secondary to the EHR journey"
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Input
          name="essentialsEyebrow"
          label="Eyebrow"
          value={data.essentialsEyebrow}
          onChange={val => onChange('essentialsEyebrow', val)}
        />
        <Input
          name="essentialsCta"
          label="CTA"
          value={data.essentialsCta}
          onChange={val => onChange('essentialsCta', val)}
        />
      </div>
      <Input
        name="essentialsHeading"
        label="Heading"
        value={data.essentialsHeading}
        onChange={val => onChange('essentialsHeading', val)}
        className="mt-3"
      />
      <Textarea
        name="essentialsSupportingCopy"
        label="Supporting copy"
        value={data.essentialsSupportingCopy}
        onChange={val => onChange('essentialsSupportingCopy', val)}
        rows={2}
        className="mt-3"
      />
      <StringListEditor
        label="Categories"
        namePrefix="essentialsCategory"
        items={data.essentialsCategories}
        onChange={items => onChange('essentialsCategories', items)}
        addLabel="Add category"
      />
      <FieldNote text="Expanding this section also reveals the Essentials feature cards configured above, so these are labels rather than links." />
    </Panel>
  );
};

// ---------------------------------------------------------------------------
// Unsure dashboard form — the home shown to trial users whose intent is
// `unsure` (or who dismissed the intent modal)
// ---------------------------------------------------------------------------

const UnsureDashboardForm: React.FC<{
  section: UnsureDashboardSectionKey;
  data: UnsureTrialDashboardContent;
  /** Read-only here: the workflow's videos and report list live on the EHR
   *  dashboard block. Used only to tell the admin which steps are unconfigured
   *  and would render as Coming soon. */
  ehrData: EhrTrialDashboardContent;
  onChange: <K extends keyof UnsureTrialDashboardContent>(
    field: K,
    value: UnsureTrialDashboardContent[K]
  ) => void;
}> = ({ section, data, ehrData, onChange }) => {
  if (section === 'intro') {
    return (
      <Panel
        title="Introduction"
        subtitle="Sets up the two paths below. Informational, so it has no CTA."
      >
        <Input
          name="introHeading"
          label="Headline"
          value={data.introHeading}
          onChange={val => onChange('introHeading', val)}
        />
        <Textarea
          name="introCopy"
          label="Supporting copy"
          value={data.introCopy}
          onChange={val => onChange('introCopy', val)}
          rows={2}
          className="mt-3"
        />
        <FieldNote text="The greeting and trial countdown above this block come from the user's own record, not from here." />
      </Panel>
    );
  }

  if (section === 'tools') {
    return (
      <Panel
        title="Clinical tools"
        subtitle="The green path - what works alongside whatever EHR they already run"
      >
        <Input
          name="toolsHeading"
          label="Section headline"
          value={data.toolsHeading}
          onChange={val => onChange('toolsHeading', val)}
        />
        <Textarea
          name="toolsSupportingCopy"
          label="Supporting copy"
          value={data.toolsSupportingCopy}
          onChange={val => onChange('toolsSupportingCopy', val)}
          rows={2}
          className="mt-3"
        />
        <Input
          name="toolsCta"
          label="Fallback button label"
          value={data.toolsCta}
          onChange={val => onChange('toolsCta', val)}
          className="mt-3"
        />
        <FieldNote text="The cards themselves are the Essentials feature cards above - same titles, descriptions and click behavior, so there is one place to edit them. Each card can set its own button label there; this one is used for any card that leaves it blank. The arrow is added by the page." />
      </Panel>
    );
  }

  if (section === 'ehr') {
    const comingSoon = [
      !ehrData.scheduleVideoUrl && 'Scheduling',
      !ehrData.billingVideoUrl && 'Billing',
      !ehrData.reportingItems.length && 'Reporting',
    ].filter(Boolean);

    return (
      <Panel
        title="Complete EHR"
        subtitle="The orange path - the whole practice workflow, plus the SAOP highlight"
      >
        <Input
          name="ehrHeading"
          label="Section headline"
          value={data.ehrHeading}
          onChange={val => onChange('ehrHeading', val)}
        />
        <Input
          name="ehrSupportingHeadline"
          label="Supporting headline"
          value={data.ehrSupportingHeadline}
          onChange={val => onChange('ehrSupportingHeadline', val)}
          className="mt-3"
        />
        <StringListEditor
          label="Benefits"
          namePrefix="ehrBenefit"
          items={data.ehrBenefits}
          onChange={items => onChange('ehrBenefits', items)}
          addLabel="Add benefit"
        />

        <Divider />

        <SubHeading text="Workflow steps" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Input
            name="schedulingTitle"
            label="Scheduling label"
            value={data.schedulingTitle}
            onChange={val => onChange('schedulingTitle', val)}
          />
          <Input
            name="schedulingCta"
            label="Scheduling CTA"
            value={data.schedulingCta}
            onChange={val => onChange('schedulingCta', val)}
          />
          <Input
            name="chartingTitle"
            label="Charting label"
            value={data.chartingTitle}
            onChange={val => onChange('chartingTitle', val)}
          />
          <Input
            name="chartingCta"
            label="Charting CTA"
            value={data.chartingCta}
            onChange={val => onChange('chartingCta', val)}
          />
          <Input
            name="unsureBillingTitle"
            label="Billing label"
            value={data.billingTitle}
            onChange={val => onChange('billingTitle', val)}
          />
          <Input
            name="unsureBillingCta"
            label="Billing CTA"
            value={data.billingCta}
            onChange={val => onChange('billingCta', val)}
          />
          <Input
            name="unsureReportingTitle"
            label="Reporting label"
            value={data.reportingTitle}
            onChange={val => onChange('reportingTitle', val)}
          />
          <Input
            name="unsureReportingCta"
            label="Reporting CTA"
            value={data.reportingCta}
            onChange={val => onChange('reportingCta', val)}
          />
        </div>
        <FieldNote text="Scheduling and Billing open the same videos as the EHR dashboard, and Reporting opens the same report list. Edit those under EHR dashboard > Learn & Demo roadmap - they are shared on purpose so the two dashboards cannot drift. Charting opens the SAOP sandbox and needs nothing configured." />
        {comingSoon.length > 0 && (
          <FieldNote
            text={`Not configured yet, so ${comingSoon.join(', ')} currently render as "Coming soon".`}
          />
        )}

        <Divider />

        <SubHeading text="SAOP highlight" />
        <Input
          name="unsureSaopHeading"
          label="Headline"
          value={data.saopHeading}
          onChange={val => onChange('saopHeading', val)}
        />
        <Textarea
          name="unsureSaopCopy"
          label="Description"
          value={data.saopCopy}
          onChange={val => onChange('saopCopy', val)}
          rows={2}
          className="mt-3"
        />
        <Input
          name="unsureSaopCta"
          label="CTA"
          value={data.saopCta}
          onChange={val => onChange('saopCta', val)}
          className="mt-3"
        />
        <FieldNote text="This CTA and the Charting step both open the SAOP sandbox - two entry points, one experience." />
        <Input
          name="unsureWhySaopCta"
          label="Why SAOP? CTA"
          value={data.whySaopCta}
          onChange={val => onChange('whySaopCta', val)}
          className="mt-3"
        />
        <FieldNote text="Why SAOP? plays the same clip as the EHR dashboard's SAOP video - shared on purpose so the two dashboards cannot drift. Set the video under EHR dashboard > The ChiroUp difference > Why SAOP? video; until it is set, this button stays hidden." />
      </Panel>
    );
  }

  return (
    <Panel
      title="Schedule a walkthrough"
      subtitle="The blue path - the human next step, available from the moment the page loads"
    >
      <Input
        name="unsureWalkthroughHeading"
        label="Headline"
        value={data.walkthroughHeading}
        onChange={val => onChange('walkthroughHeading', val)}
      />
      <Textarea
        name="unsureWalkthroughCopy"
        label="Supporting copy"
        value={data.walkthroughCopy}
        onChange={val => onChange('walkthroughCopy', val)}
        rows={2}
        className="mt-3"
      />
      <Input
        name="unsureWalkthroughCta"
        label="CTA"
        value={data.walkthroughCta}
        onChange={val => onChange('walkthroughCta', val)}
        className="mt-3"
      />
      <FieldNote text="Opens the scheduler link from Page settings, with the user's name, email and clinic carried in so they are not retyping what we already know." />
    </Panel>
  );
};

const AddRowButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-dark-gray-400 dark:hover:bg-dark-gray-700 dark:hover:text-dark-gray-200 transition-colors"
  >
    <Icon icon={Icons.add} className="h-3 w-3" />
    {label}
  </button>
);

/**
 * Three fixed upload slots for the walkthrough avatars. The stored value is a
 * plain array, so slots are the first three entries and clearing one closes
 * the gap — "the first three uploaded faces" rather than three addressable
 * positions, which is all the overlapping cluster needs.
 */
const AvatarUploaders: React.FC<{
  avatars: string[];
  onChange: (avatars: string[]) => void;
}> = ({ avatars, onChange }) => {
  const slots = [0, 1, 2].map(i => avatars[i] ?? '');

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {slots.map((src, idx) => (
        <Uploader
          key={`walkthroughAvatar-${idx}`}
          name={`walkthroughAvatar-${idx}`}
          label={`Face ${idx + 1}`}
          className=""
          value={src}
          onChange={val => {
            const next = [...slots];
            next[idx] = val ?? '';
            onChange(next.filter(Boolean));
          }}
          type="image"
          uploadCategory="loginContent"
          uploadType="image"
          fileTypes={['png', 'jpg', 'jpeg', 'gif', 'webp']}
          fileSizeLimit={10}
          square
        />
      ))}
    </div>
  );
};

const SubHeading: React.FC<{ text: string }> = ({ text }) => (
  <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
    {text}
  </h3>
);

const FieldNote: React.FC<{ text: string }> = ({ text }) => (
  <p className="mt-2 text-xs text-gray-500 dark:text-dark-gray-400">{text}</p>
);

/**
 * Add / edit / remove rows of a plain string list. Order is the render order,
 * so removing a row and re-adding it is how you reorder — these lists are
 * three or four items long, which isn't worth drag handles.
 */
const StringListEditor: React.FC<{
  label: string;
  namePrefix: string;
  items: string[];
  addLabel: string;
  onChange: (items: string[]) => void;
}> = ({ label, namePrefix, items, addLabel, onChange }) => (
  <div className="mt-3">
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-sm text-gray-700 dark:text-dark-gray-200">{label}</span>
      <AddRowButton label={addLabel} onClick={() => onChange([...items, ''])} />
    </div>
    {items.length === 0 && <FieldNote text="Nothing listed yet." />}
    {items.map((item, idx) => (
      <div key={`${namePrefix}-${idx}`} className="flex items-start gap-2 mt-2">
        <div className="flex-1">
          <Input
            name={`${namePrefix}-${idx}`}
            value={item}
            onChange={val => onChange(items.map((v, i) => (i === idx ? val : v)))}
          />
        </div>
        <IconButton
          icon={<Icon icon={Icons.trash} className="h-3.5 w-3.5" />}
          label={`Remove ${label} ${idx + 1}`}
          onClick={() => onChange(items.filter((_, i) => i !== idx))}
          tone="danger"
        />
      </div>
    ))}
  </div>
);

const FaqListEditor: React.FC<{
  faqs: EhrDashboardFaq[];
  onChange: (faqs: EhrDashboardFaq[]) => void;
}> = ({ faqs, onChange }) => (
  <div>
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-sm text-gray-700 dark:text-dark-gray-200">Questions & answers</span>
      <AddRowButton
        label="Add FAQ"
        onClick={() => onChange([...faqs, { question: '', answer: '' }])}
      />
    </div>
    {faqs.map((faq, idx) => (
      <div
        key={`faq-${idx}`}
        className="mt-2 rounded-md border border-gray-200 dark:border-dark-gray-700 p-3"
      >
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <Input
              name={`faq-question-${idx}`}
              label={`Question ${idx + 1}`}
              value={faq.question}
              onChange={val =>
                onChange(faqs.map((f, i) => (i === idx ? { ...f, question: val } : f)))
              }
            />
          </div>
          <IconButton
            icon={<Icon icon={Icons.trash} className="h-3.5 w-3.5" />}
            label={`Remove FAQ ${idx + 1}`}
            onClick={() => onChange(faqs.filter((_, i) => i !== idx))}
            tone="danger"
          />
        </div>
        <Textarea
          name={`faq-answer-${idx}`}
          label="Answer"
          value={faq.answer}
          onChange={val => onChange(faqs.map((f, i) => (i === idx ? { ...f, answer: val } : f)))}
          rows={3}
          className="mt-3"
        />
        {!faq.answer && <FieldNote text="Unanswered rows expand to a placeholder on the page." />}
      </div>
    ))}
  </div>
);

const ReportingItemsEditor: React.FC<{
  items: EhrReportingItem[];
  onChange: (items: EhrReportingItem[]) => void;
}> = ({ items, onChange }) => (
  <div className="mt-3">
    <div className="flex items-center justify-between mb-1.5">
      <span className="text-sm text-gray-700 dark:text-dark-gray-200">Report types</span>
      <AddRowButton
        label="Add report type"
        onClick={() => onChange([...items, { title: '', description: '' }])}
      />
    </div>
    {items.length === 0 && <FieldNote text="Nothing listed yet." />}
    {items.map((item, idx) => (
      <div
        key={`reporting-${idx}`}
        className="mt-2 rounded-md border border-gray-200 dark:border-dark-gray-700 p-3"
      >
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <Input
              name={`reporting-title-${idx}`}
              label={`Report ${idx + 1}`}
              value={item.title}
              onChange={val =>
                onChange(items.map((r, i) => (i === idx ? { ...r, title: val } : r)))
              }
            />
          </div>
          <IconButton
            icon={<Icon icon={Icons.trash} className="h-3.5 w-3.5" />}
            label={`Remove report ${idx + 1}`}
            onClick={() => onChange(items.filter((_, i) => i !== idx))}
            tone="danger"
          />
        </div>
        <Textarea
          name={`reporting-description-${idx}`}
          label="Description"
          value={item.description}
          onChange={val =>
            onChange(items.map((r, i) => (i === idx ? { ...r, description: val } : r)))
          }
          rows={2}
          className="mt-3"
        />
      </div>
    ))}
  </div>
);

// ---------------------------------------------------------------------------
// Home card form
// ---------------------------------------------------------------------------

const HomeCardForm: React.FC<{
  card: TrialHomeCard;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onUpdate: (patch: Partial<TrialHomeCard>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
}> = ({ card, canMoveUp, canMoveDown, onUpdate, onMoveUp, onMoveDown, onDelete }) => (
  <Panel
    title={card.title || 'Untitled card'}
    subtitle={card.id}
    rightSide={
      <div className="flex items-center gap-1">
        <IconButton
          icon={<ArrowUpIcon className="h-3.5 w-3.5" />}
          label="Move up"
          onClick={onMoveUp}
          disabled={!canMoveUp}
        />
        <IconButton
          icon={<ArrowDownIcon className="h-3.5 w-3.5" />}
          label="Move down"
          onClick={onMoveDown}
          disabled={!canMoveDown}
        />
        <IconButton
          icon={<Icon icon={Icons.trash} className="h-3.5 w-3.5" />}
          label="Delete card"
          onClick={onDelete}
          tone="danger"
        />
      </div>
    }
  >
    <div className="flex items-center justify-between mb-1">
      <Checkbox
        name={`enabled-${card.id}`}
        label="Visible to users"
        value={card.enabled}
        onChange={val => onUpdate({ enabled: val })}
      />
    </div>

    <Divider />

    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
      Content
    </h3>
    <Input
      name={`title-${card.id}`}
      label="Title"
      value={card.title}
      onChange={val => onUpdate({ title: val })}
    />
    <Textarea
      name={`description-${card.id}`}
      label="Description"
      value={card.description}
      onChange={val => onUpdate({ description: val })}
      rows={2}
      className="mt-3"
    />
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
      <Input
        name={`badge-${card.id}`}
        label='Badge (optional, e.g., "Start here")'
        value={card.badge || ''}
        onChange={val => onUpdate({ badge: val })}
      />
      <Input
        name={`ctaLabel-${card.id}`}
        label="Button label (unsure dashboard only)"
        value={card.ctaLabel || ''}
        onChange={val => onUpdate({ ctaLabel: val })}
      />
      <Select
        name={`icon-${card.id}`}
        label="Icon"
        value={card.icon}
        options={TRIAL_CARD_ICON_OPTIONS.map(o => ({ text: o.label, value: o.id }))}
        onChange={(v: string) => onUpdate({ icon: v as TrialCardIcon })}
        alwaysReturnArray={false}
      />
    </div>
    <Select
      name={`section-${card.id}`}
      label="Section"
      value={card.section}
      options={SECTION_OPTIONS}
      onChange={(v: string) => onUpdate({ section: v as TrialCardSection })}
      alwaysReturnArray={false}
      className="mt-3"
    />

    <Divider />

    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-3">
      On click
    </h3>
    <ClickBehaviorEditor
      cardId={card.id}
      value={card.clickBehavior}
      onChange={cb => onUpdate({ clickBehavior: cb })}
    />

    <Divider />

    <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-dark-gray-400 mb-1">
      Secondary video CTA
    </h3>
    <p className="text-xs text-gray-500 dark:text-dark-gray-400 mb-3">
      Optional smaller gray button inside the card that opens this video in a modal. The button only
      shows when both fields are filled in.
    </p>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      <Input
        name={`secondary-cta-label-${card.id}`}
        label='Button label (e.g., "Why SOAP Is Different")'
        value={card.secondaryCta?.label || ''}
        onChange={(val: string) =>
          onUpdate({
            secondaryCta: { label: val, videoUrl: card.secondaryCta?.videoUrl || '' },
          })
        }
      />
      <Input
        name={`secondary-cta-video-${card.id}`}
        label="Video URL (YouTube / Loom / Vimeo)"
        value={card.secondaryCta?.videoUrl || ''}
        onChange={(val: string) =>
          onUpdate({
            secondaryCta: { label: card.secondaryCta?.label || '', videoUrl: val },
          })
        }
      />
    </div>
  </Panel>
);

// ---------------------------------------------------------------------------
// Tour step form
// ---------------------------------------------------------------------------

const TourStepForm: React.FC<{
  tour: TrialTourManifest;
  step: TrialTourStepManifest;
  stepNumber: number;
  override: StepOverride;
  onChangeField: (field: keyof StepOverride, value: string) => void;
  onReset: () => void;
}> = ({ tour, step, stepNumber, override, onChangeField, onReset }) => {
  const edited = hasAnyOverride(override);

  return (
    <Panel
      title={`${tour.label} — Step ${stepNumber}`}
      subtitle={step.hint || step.stepKey}
      rightSide={
        edited ? (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-gray-300 dark:border-dark-gray-600 hover:bg-gray-50 dark:hover:bg-dark-gray-700 text-xs font-medium text-gray-600 dark:text-dark-gray-300 transition-colors shrink-0"
          >
            <ArrowUturnLeftIcon className="h-3 w-3" />
            Reset to default
          </button>
        ) : null
      }
    >
      <p className="text-xs text-gray-500 dark:text-dark-gray-400 mb-4">
        Anchors and step order are wired in code — marketing only edits the words. Leave a field
        blank to use the code default.
      </p>

      <OverridableField
        label="Title"
        stepKey={step.stepKey}
        field="title"
        defaultValue={step.title}
        override={override.title}
        onChange={value => onChangeField('title', value)}
      />
      <OverridableField
        label="Body"
        stepKey={step.stepKey}
        field="body"
        defaultValue={step.body}
        override={override.body}
        onChange={value => onChangeField('body', value)}
        multiline
        rows={5}
        help='Plain text. Use blank lines for paragraph breaks. Wrap a word in **stars** for bold (e.g., "**Plan** — evidence-based treatments").'
      />
      <OverridableField
        label="Pro tip (italic line under the body)"
        stepKey={step.stepKey}
        field="proTip"
        defaultValue={step.proTip}
        override={override.proTip}
        onChange={value => onChangeField('proTip', value)}
        multiline
        rows={2}
      />
      <OverridableField
        label="Next button label"
        stepKey={step.stepKey}
        field="nextLabel"
        defaultValue={step.nextLabel}
        override={override.nextLabel}
        onChange={value => onChangeField('nextLabel', value)}
        help={'Leave blank for the default ("Next" / "Finish").'}
      />
      <OverridableField
        label="Video URL"
        stepKey={step.stepKey}
        field="video"
        defaultValue={step.video}
        override={override.video}
        onChange={value => onChangeField('video', value)}
        help="Paste a YouTube, Loom, or Vimeo share link to embed a video in this step. Leave blank for none."
      />
    </Panel>
  );
};

const OverridableField: React.FC<{
  label: string;
  stepKey: string;
  field: keyof StepOverride;
  defaultValue?: string;
  override?: string;
  onChange: (value: string) => void;
  multiline?: boolean;
  rows?: number;
  help?: string;
}> = ({ label, stepKey, field, defaultValue, override, onChange, multiline, rows = 2, help }) => {
  const effective = override ?? defaultValue ?? '';
  const isOverridden = typeof override === 'string';
  const name = `${field}-${stepKey}`;
  const hint = help
    ? help
    : defaultValue
      ? `Default: "${defaultValue.replace(/\n+/g, ' / ')}"`
      : 'No code default — admin-only field.';

  return (
    <div className="mb-4 last:mb-0">
      <div className="flex items-center justify-between mb-1">
        <label className="text-xs font-medium text-gray-700 dark:text-dark-gray-200">{label}</label>
        {isOverridden && (
          <span className="text-[10px] font-medium text-amber-700 dark:text-amber-400">
            overriding default
          </span>
        )}
      </div>
      {multiline ? (
        <Textarea
          name={name}
          label=""
          value={effective}
          onChange={(v: string) => onChange(v)}
          rows={rows}
        />
      ) : (
        <Input name={name} label="" value={effective} onChange={(v: string) => onChange(v)} />
      )}
      <p className="mt-1 text-[10px] text-gray-400 dark:text-dark-gray-500 truncate">{hint}</p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Click-behavior editor (route paths validated inline — appDataUpdate also
// enforces this on the backend, see clickBehaviorSchema in
// libs/.../appDataUpdate.ts).
// ---------------------------------------------------------------------------

const ClickBehaviorEditor: React.FC<{
  cardId: string;
  value?: ClickBehavior;
  onChange: (cb: ClickBehavior) => void;
}> = ({ cardId, value, onChange }) => {
  const kind = value?.kind ?? 'none';

  const setKind = (next: string) => {
    switch (next) {
      case 'tour':
        onChange({ kind: 'tour', tourId: TRIAL_TOUR_OPTIONS[0]?.id ?? '' });
        break;
      case 'video':
        onChange({ kind: 'video', url: '' });
        break;
      case 'route':
        onChange({ kind: 'route', path: '' });
        break;
      case 'external':
        onChange({ kind: 'external', url: '' });
        break;
      case 'none':
      default:
        onChange({ kind: 'none' });
        break;
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <Select
        name={`click-kind-${cardId}`}
        label="Behavior"
        value={kind}
        options={CLICK_KIND_OPTIONS}
        onChange={(v: string) => setKind(v)}
        alwaysReturnArray={false}
      />
      {value?.kind === 'tour' && (
        <Select
          name={`click-tour-${cardId}`}
          label="Tour"
          value={value.tourId}
          options={TRIAL_TOUR_OPTIONS.map(t => ({ text: t.label, value: t.id }))}
          onChange={(v: string) => onChange({ kind: 'tour', tourId: v })}
          alwaysReturnArray={false}
        />
      )}
      {value?.kind === 'video' && (
        <>
          <Input
            name={`click-video-url-${cardId}`}
            label="Video URL (YouTube share link, Loom share link, or any embed URL)"
            value={value.url}
            onChange={(v: string) => onChange({ kind: 'video', url: v, title: value.title })}
          />
          <Input
            name={`click-video-title-${cardId}`}
            label="Video title (shown in the modal header, optional)"
            value={value.title || ''}
            onChange={(v: string) => onChange({ kind: 'video', url: value.url, title: v })}
          />
        </>
      )}
      {value?.kind === 'route' && (
        <Input
          name={`click-route-${cardId}`}
          label="Path (e.g., /patients, /schedule, /billing)"
          value={value.path}
          onChange={(v: string) => onChange({ kind: 'route', path: v })}
          errors={
            value.path && !value.path.startsWith('/')
              ? { message: 'Path must start with "/" (e.g., /patients).' }
              : undefined
          }
        />
      )}
      {value?.kind === 'external' && (
        <Input
          name={`click-external-${cardId}`}
          label="External URL (opens in a new tab, e.g., https://chiroup.com)"
          value={value.url}
          onChange={(v: string) => onChange({ kind: 'external', url: v })}
          errors={
            value.url && !/^https?:\/\//i.test(value.url)
              ? { message: 'URL must start with http:// or https://' }
              : undefined
          }
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Shared UI primitives
// ---------------------------------------------------------------------------

const Panel: React.FC<{
  title: string;
  subtitle?: string;
  rightSide?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, subtitle, rightSide, children }) => (
  <div className="bg-white dark:bg-dark-gray-800 border border-gray-200 dark:border-dark-gray-700 rounded-lg p-5">
    <div className="flex items-start justify-between gap-3 mb-4">
      <div className="min-w-0">
        <h2 className="text-base font-semibold text-gray-900 dark:text-dark-gray-100 truncate">
          {title}
        </h2>
        {subtitle && (
          <p className="mt-0.5 text-xs font-mono text-gray-400 dark:text-dark-gray-500 truncate">
            {subtitle}
          </p>
        )}
      </div>
      {rightSide}
    </div>
    {children}
  </div>
);

const Divider: React.FC = () => (
  <div className="my-4 border-t border-gray-100 dark:border-dark-gray-700" />
);

const IconButton: React.FC<{
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  tone?: 'default' | 'danger';
}> = ({ icon, label, onClick, disabled, tone = 'default' }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className={[
      'p-1.5 rounded-md border transition-colors disabled:opacity-30 disabled:cursor-not-allowed',
      tone === 'danger'
        ? 'border-gray-300 dark:border-dark-gray-600 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:hover:text-red-400'
        : 'border-gray-300 dark:border-dark-gray-600 text-gray-500 hover:text-gray-900 hover:bg-gray-50 dark:hover:bg-dark-gray-700 dark:hover:text-dark-gray-100',
    ].join(' ')}
  >
    {icon}
  </button>
);

// Tri-state status: in-memory dirty > saved-but-not-deployed > clean.
// Same priority order the home page builder uses so the two editors feel
// consistent to whoever's switching between them.
const DeployStatusPill: React.FC<{
  isDirty: boolean;
  hasUnpublished: boolean;
  saveMessage: string;
}> = ({ isDirty, hasUnpublished, saveMessage }) => {
  if (saveMessage === 'Saved!' || saveMessage === 'Deployed!') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
        <Icon icon={Icons.checkCircle} className="h-4 w-4" />
        {saveMessage === 'Deployed!' ? 'Deployed' : 'Saved'}
      </span>
    );
  }

  if (saveMessage === 'Nothing to deploy') {
    return <span className="text-xs font-medium text-gray-500">Nothing to deploy</span>;
  }

  if (saveMessage === 'Error saving' || saveMessage === 'Error deploying') {
    return <span className="text-xs font-medium text-red-600">{saveMessage}</span>;
  }

  if (isDirty) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        Unsaved changes
      </span>
    );
  }

  if (hasUnpublished) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 dark:text-blue-400">
        <span className="h-1.5 w-1.5 rounded-full bg-blue-500" />
        Unpublished draft
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-400">
      <span className="h-1.5 w-1.5 rounded-full bg-gray-300" />
      Up to date
    </span>
  );
};

// ---------------------------------------------------------------------------
// Preview modal
// ---------------------------------------------------------------------------

const PreviewModal: React.FC<{
  open: boolean;
  data: TrialHomeContent;
  selectedCardId: string | null;
  /** Which trial home to draw. Follows the sidebar selection so previewing
   *  while editing EHR copy shows the EHR dashboard, not the card home. */
  variant: 'cards' | 'ehr' | 'unsure';
  onSelectCard: (id: string) => void;
  onClose: () => void;
}> = ({ open, data, selectedCardId, variant, onSelectCard, onClose }) => {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Trial home preview"
    >
      <div
        className="relative w-full max-w-5xl max-h-[90vh] bg-white dark:bg-dark-gray-900 rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-dark-gray-700 bg-gray-50 dark:bg-dark-gray-800">
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full bg-red-300/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-yellow-300/80" />
            <span className="h-2.5 w-2.5 rounded-full bg-green-300/80" />
            <span className="ml-3 text-[11px] font-mono uppercase tracking-wider text-gray-500">
              {variant === 'ehr'
                ? 'EHR dashboard — preview'
                : variant === 'unsure'
                  ? 'Unsure dashboard — preview'
                  : 'Trial home — preview'}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-dark-gray-700 transition-colors"
            aria-label="Close preview"
          >
            <Icon icon={Icons.close} className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {variant === 'ehr' ? (
            <EhrTrialHomePreview data={data} />
          ) : variant === 'unsure' ? (
            <UnsureTrialHomePreview data={data} />
          ) : (
            <TrialHomePreview
              data={data}
              selectedCardId={selectedCardId}
              onSelectCard={onSelectCard}
            />
          )}
        </div>
        <div className="px-5 py-2.5 border-t border-gray-100 dark:border-dark-gray-700 bg-gray-50 dark:bg-dark-gray-800 text-[11px] text-gray-500 dark:text-dark-gray-400">
          {variant === 'ehr'
            ? 'Shown to trial users whose intent is EHR, as they see it on their first visit.'
            : variant === 'unsure'
              ? 'Shown to trial users who are unsure, or who dismissed the intent question.'
              : 'Click any card to jump to its editor.'}
        </div>
      </div>
    </div>,
    document.body
  );
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const hasAnyOverride = (override?: StepOverride): boolean =>
  !!override &&
  (typeof override.title === 'string' ||
    typeof override.body === 'string' ||
    typeof override.proTip === 'string' ||
    typeof override.nextLabel === 'string' ||
    typeof override.video === 'string');

// Drop empty step/tour records so the stored blob stays compact and
// `Object.keys` checks remain meaningful at render time.
function pruneEmpty(overrides: OverridesByTour): OverridesByTour {
  const out: OverridesByTour = {};
  for (const [tourId, steps] of Object.entries(overrides)) {
    const cleanedSteps: Record<string, StepOverride> = {};
    for (const [stepKey, stepOverride] of Object.entries(steps)) {
      const nonEmpty = Object.fromEntries(
        Object.entries(stepOverride).filter(([, v]) => typeof v === 'string' && v.trim() !== '')
      );
      if (Object.keys(nonEmpty).length > 0) cleanedSteps[stepKey] = nonEmpty as StepOverride;
    }
    if (Object.keys(cleanedSteps).length > 0) out[tourId] = cleanedSteps;
  }

  return out;
}

export default TrialExperienceEditor;
